// Repository seam. The UI talks only to `repo`; the implementation is either
// cloud (Firestore/GCS via the function) or local (localStorage). Local is the
// default in dev so `npm run dev` works with no backend; prod builds use cloud.
// Force either with VITE_PERSISTENCE=cloud|local.

import type { AtomStatus, EventBrief, NewsAtom, Source, StoryEvent, Topic } from './types';
import { cloudRepo } from './cloudRepo';
import { localRepo } from './localRepo';

export interface TopicBundle {
  topic: Topic;
  sources: Source[];
  atoms: NewsAtom[];
  events?: StoryEvent[];
}

export interface Repo {
  listTopics(): Promise<Topic[]>;
  createTopic(input: { title: string; description?: string }): Promise<Topic>;
  renameTopic(topicId: string, title: string): Promise<{ title: string }>;
  /** Auto-name a topic from its analyzed content (Gemini; cloud only). */
  suggestTitle(topicId: string): Promise<{ title: string }>;
  getBundle(topicId: string): Promise<TopicBundle>;
  saveSources(topicId: string, sources: Source[], csvRaw: string): Promise<void>;
  /** Delete one source (its record + stored files); atoms are left to re-atomize. */
  deleteSource(topicId: string, sourceId: string): Promise<void>;
  saveAtoms(topicId: string, atoms: NewsAtom[]): Promise<void>;
  setAtomStatus(atomId: string, status: AtomStatus): Promise<void>;
  deleteTopic(topicId: string): Promise<void>;
  /** Start server-side LLM atomization as a background job (cloud only). */
  startAtomize(topicId: string): Promise<JobHandle>;
  /** Publish the topic's atoms to Iconik for the Story Engine — background job (cloud only). */
  publishToIconik(topicId: string): Promise<JobHandle>;
  /** Delegate a footage production to the Story Engine over its MCP — background job (cloud only). */
  produce(topicId: string, opts?: ProduceOptions): Promise<JobHandle>;
  /** Produce a CNN vertical from a picked EVENT (scoped to its atoms' clips) — background job (cloud only). */
  produceEvent(topicId: string, eventId: string, opts?: ProduceOptions): Promise<JobHandle>;
  /** Poll a background job's status/result (cloud only). */
  getJob(jobId: string): Promise<JobState>;
  /** Story Engine genres + aspects for the produce picker (cloud only). */
  getCapabilities(): Promise<Capabilities>;
  /** Prior Story Engine productions launched for this topic (cloud only). */
  listProductions(topicId: string): Promise<{ productions: ProductionRecord[] }>;
  /** Register a file + get a signed GCS upload URL (cloud only). */
  ingestUrl(topicId: string, filename: string, contentType: string): Promise<IngestUrlResult>;
  /** Analyze an uploaded file into text + mirror it into Iconik — background job (cloud only). */
  startAnalyze(topicId: string, sourceId: string, contentType: string): Promise<JobHandle>;
  /** Signed READ URL to preview a source clip (cloud only). */
  mediaUrl(topicId: string, sourceId: string): Promise<MediaUrlResult>;
  /** Cluster the topic's atoms into events (Gemini) — background job (cloud only). */
  clusterEvents(topicId: string): Promise<JobHandle>;
  /** Persist manually-aggregated events (the human aggregator). Replaces the topic's events. */
  saveEvents(topicId: string, events: StoryEvent[]): Promise<{ events: StoryEvent[] }>;
  /** Generate production-ready info for an assembled event via AI (cloud only). */
  enrichEvent(topicId: string, input: EnrichEventInput): Promise<EventEnrichment>;
  /** Read the cross-topic Event Bank (cloud only). */
  getEventBank(): Promise<EventBankResult>;
  /** Rebuild the cross-topic Event Bank across all your topics (Gemini; cloud only). */
  rebuildEventBank(): Promise<RebuildBankResult>;
  /** Context Repository — CNN standing editorial guidance ("the recipe"). Org-wide. */
  listRecipes(includeArchived?: boolean): Promise<{ recipes: Recipe[] }>;
  createRecipe(input: RecipeInput): Promise<Recipe>;
  updateRecipe(recipeId: string, input: Partial<RecipeInput> & { status?: 'active' | 'archived' }): Promise<Recipe>;
  archiveRecipe(recipeId: string): Promise<Recipe>;
}

export type RecipeKind =
  | 'playbook'
  | 'editing_spec'
  | 'brand_rule'
  | 'style_rule'
  | 'business_rule'
  | 'example'
  | 'performance_data';

export const RECIPE_KINDS: RecipeKind[] = [
  'playbook',
  'editing_spec',
  'brand_rule',
  'style_rule',
  'business_rule',
  'example',
  'performance_data',
];

export const RECIPE_KIND_LABELS: Record<RecipeKind, string> = {
  playbook: 'Playbook',
  editing_spec: 'Editing Spec',
  brand_rule: 'Brand Rule',
  style_rule: 'Style Rule',
  business_rule: 'Business Rule',
  example: 'Reference Example',
  performance_data: 'Performance Data',
};

export interface RecipeAppliesTo {
  genres: string[];
  aspects: string[];
  source_conditions: string[];
}

export interface Recipe {
  recipe_id: string;
  kind: RecipeKind;
  title: string;
  body: string;
  applies_to: RecipeAppliesTo;
  tags: string[];
  version: number;
  status: 'active' | 'archived';
  content_sha256: string;
  origin: { author: string; created_at: number; updated_at: number };
}

export interface RecipeInput {
  kind: RecipeKind;
  title: string;
  body: string;
  applies_to?: Partial<RecipeAppliesTo>;
  tags?: string[];
}

/** Provenance stamp on a production: which recipe/version shaped it. */
export interface RecipeRef {
  recipe_id: string;
  kind: RecipeKind;
  title: string;
  version: number;
  content_sha256: string;
}

export interface EnrichEventInput {
  atomIds: string[];
  label?: string;
  /** Optional editorial steer for the enrichment. */
  hint?: string;
}

export interface EventEnrichment {
  ok: boolean;
  label: string;
  frame: { actor: string; action: string; object: string; location: string };
  brief: EventBrief;
}

export interface BankEventRef {
  event_id: string;
  topic_id: string;
  topic_title: string;
  label: string;
}

export interface BankEntry {
  bank_id: string;
  label: string;
  actor?: string;
  action?: string;
  object?: string;
  location?: string;
  event_refs: BankEventRef[];
  topic_ids: string[];
  cross_topic: boolean;
  createdAt?: number;
}

export interface EventBankResult {
  entries: BankEntry[];
}

export interface RebuildBankResult {
  count: number;
  crossTopic: number;
  iconikMirrored?: boolean;
  entries: BankEntry[];
}

export interface MediaUrlResult {
  url: string;
  mediaType: string;
}

export interface IngestUrlResult {
  sourceId: string;
  uploadUrl: string;
  gcsPath: string;
  kind: string;
}

export interface IconikPublishResult {
  count: number;
  collectionId: string;
  /** The collection Story Engine ingests for a footage production (video sources at top level). */
  footageCollectionId?: string;
  footageMirrored?: number;
  assetId: string;
  versioned?: boolean;
  events?: number;
  sourcesMirrored?: number;
  skipped?: string[];
}

export interface JobHandle {
  jobId: string;
  status?: string;
}

export interface JobState {
  jobId: string;
  type: 'atomize' | 'cluster-events' | 'publish-iconik' | 'produce';
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  progress?: { done: number; total: number; note?: string };
  result?: unknown;
  error?: string;
}

export interface GenreOption {
  genre_key: string;
  display_name: string;
  footage: boolean;
}

export interface Capabilities {
  connected: boolean;
  genres: GenreOption[];
  footageGenres: string[];
  aspects: string[];
  note?: string;
}

export interface ProduceOptions {
  aspect?: string;
  genre?: string;
  durationS?: number;
  brief?: string;
  /** Ground the production with the Context Repository (default true). Off = A/B baseline. */
  grounded?: boolean;
}

export interface ProductionRecord {
  production_id: string;
  project_id: string;
  topic_id: string;
  title: string;
  aspect: string;
  genre: string;
  footage_collection_id: string;
  url: string;
  grounded?: boolean;
  recipe_refs?: RecipeRef[];
  launched_by_uid: string;
  launched_by_email: string;
  createdAt: number;
}

export interface ProduceResult {
  ok?: boolean;
  production_id?: string;
  project_id?: string;
  /** Deep link into the Story Engine UI, if the engine returns one. */
  url?: string;
  record?: ProductionRecord;
}

const MODE = (import.meta.env.VITE_PERSISTENCE as string | undefined) ?? (import.meta.env.PROD ? 'cloud' : 'local');

export const persistenceMode: 'cloud' | 'local' = MODE === 'cloud' ? 'cloud' : 'local';
export const repo: Repo = persistenceMode === 'cloud' ? cloudRepo : localRepo;
